/**
 * @fileoverview AR-924 — Rich tooltip of a deliverable cell: who submitted, when, attachments, the
 * teacher-assessment state of the lesson and the click hint. Pure presentational.
 */

import { Group, Stack, Text, ThemeIcon } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import type { TaskTrackCellDelivery, TrackStatus } from '../types'
import { STATUS_COLOR } from './TableCells'
import { formatDate } from './tableHelpers'

/** Figma: 4px bullet in the status color, 6px before the assessment line. */
const STATUS_DOT_SIZE = 6
const LINE_SPACING = 4

interface DeliverableCellTooltipProps {
  readonly cellDelivery: TaskTrackCellDelivery | null | undefined
  readonly completed: boolean
  readonly studentFirstName: string
  /** Logged-in user's id, matched against the cell's `uploadedById` for "Uploaded by you". */
  readonly currentUserId?: string
  readonly teacherAssessmentStatus: TrackStatus
}

type Translate = (key: string, options?: object) => string

function countLabel(tr: Translate, key: string, count: number): string {
  const plural = count === 1 ? 'one' : 'other'
  return tr(`${key}_${plural}`, { count })
}

/** By id, never by name: two teachers can share a name. */
function uploadedByCurrentUser(cellDelivery: TaskTrackCellDelivery, currentUserId?: string): boolean {
  if (cellDelivery.uploadedByCurrentUser !== undefined) return cellDelivery.uploadedByCurrentUser
  return Boolean(currentUserId) && cellDelivery.uploadedById === currentUserId
}

function titleOf(props: DeliverableCellTooltipProps, tr: Translate): string {
  const { cellDelivery, completed, studentFirstName, currentUserId } = props
  if (!completed) return tr('pending')
  if (!cellDelivery?.uploadedByTeacher) return tr('submitted_by', { name: studentFirstName })
  if (uploadedByCurrentUser(cellDelivery, currentUserId)) {
    return tr('uploaded_by_you_for', { name: studentFirstName })
  }
  return tr('uploaded_by_for', { teacher: cellDelivery.uploadedByName ?? '', name: studentFirstName })
}

function detailsOf(cellDelivery: TaskTrackCellDelivery | null | undefined, tr: Translate, locale: string): string[] {
  if (!cellDelivery) return []
  const details: string[] = []
  if (cellDelivery.workCompletedAt) {
    details.push(tr('work_completed', { date: formatDate(cellDelivery.workCompletedAt, locale) }))
  }
  if (cellDelivery.filesCount > 0) details.push(countLabel(tr, 'file_count', cellDelivery.filesCount))
  if (cellDelivery.linksCount > 0) details.push(countLabel(tr, 'link_count', cellDelivery.linksCount))
  return details
}

export function DeliverableCellTooltip(props: DeliverableCellTooltipProps) {
  const { cellDelivery, teacherAssessmentStatus } = props
  const { t, i18n } = useTranslation()
  const tr: Translate = (key, options) => t(`task_tracker.cell_tooltip.${key}`, options)
  const title = titleOf(props, tr)
  const details = detailsOf(cellDelivery, tr, i18n.language)

  return (
    <Stack spacing={LINE_SPACING} align="flex-start" data-testid="deliverable-cell-tooltip">
      <Text size="sm" weight={600}>{title}</Text>
      {details.length > 0 && (
        <Text size="xs" sx={theme => ({ color: theme.colors.gray[4] })}>{details.join(' · ')}</Text>
      )}
      <Group spacing={STATUS_DOT_SIZE} noWrap align="center">
        <ThemeIcon size={STATUS_DOT_SIZE} radius="xl" color={STATUS_COLOR[teacherAssessmentStatus]} aria-hidden>
          <span />
        </ThemeIcon>
        <Text size="xs" color={STATUS_COLOR[teacherAssessmentStatus]} weight={500}>
          {tr(`teacher_assessment_${teacherAssessmentStatus}`)}
        </Text>
      </Group>
      <Text size="xs">{tr('click_to_open')}</Text>
    </Stack>
  )
}
